import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { 
  obtenerSolicitudes, 
  marcarComoLeida, 
  actualizarEstadoSolicitud,
  crearClienteDesdeSolicitud,
  TIPOS_ASESORIA,
  obtenerEstadisticasSolicitudes
} from '../../services/solicitudService';
import AdminLayout from '../../components/layout/AdminLayout';
import BackButton from '../../components/common/BackButton';
import NotificationModal from '../../components/common/NotificationModal';
import './SolicitudesAdmin.css';

const SolicitudesAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todas');
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    pendientes: 0,
    noLeidas: 0,
    completadas: 0
  });
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  const showNotification = (title, message, type = 'success') => {
    setNotification({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeNotification = () => {
    setNotification({ ...notification, isOpen: false });
  };

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [solicitudesData, estadisticasData] = await Promise.all([
        obtenerSolicitudes(),
        obtenerEstadisticasSolicitudes()
      ]);
      setSolicitudes(solicitudesData);
      setEstadisticas(estadisticasData);
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error', 'Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarDatos();
  }, [user, navigate, cargarDatos]);

  const handleMarcarLeida = async (id) => {
    try {
      await marcarComoLeida(id);
      await cargarDatos();
      showNotification('Exito', 'Solicitud marcada como leida');
    } catch (error) {
      showNotification('Error', 'Error al marcar como leida', 'error');
    }
  };

  const handleCambiarEstado = async (id, estado) => {
    try {
      await actualizarEstadoSolicitud(id, estado);
      await cargarDatos();
      showNotification('Estado Actualizado', `Solicitud marcada como ${estado}`);
    } catch (error) {
      showNotification('Error', 'Error al cambiar estado', 'error');
    }
  };

  const handleCrearCliente = async (solicitud) => {
    try {
      await crearClienteDesdeSolicitud(solicitud);
      await actualizarEstadoSolicitud(solicitud.id, 'cliente_creado');
      await cargarDatos();
      showNotification('Cliente Creado', `Se ha creado el cliente ${solicitud.nombre}`);
    } catch (error) {
      showNotification('Error', 'Error al crear cliente', 'error');
    }
  };

  const handleVerDetalle = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
    setModalDetalle(true);
    if (!solicitud.leido) {
      handleMarcarLeida(solicitud.id);
    }
  };

  const handleContactar = (solicitud) => {
    navigator.clipboard.writeText(solicitud.telefono);
    showNotification('Telefono copiado', `Telefono ${solicitud.telefono} copiado al portapapeles`);
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      [TIPOS_ASESORIA.COMPRA]: 'Compra de Vehiculo',
      [TIPOS_ASESORIA.VENTA]: 'Venta de Vehiculo',
      [TIPOS_ASESORIA.VERIFICACION]: 'Verificacion Legal',
      [TIPOS_ASESORIA.TRASPASO]: 'Tramite de Traspaso',
      [TIPOS_ASESORIA.OTRO]: 'Otro'
    };
    return tipos[tipo] || tipo;
  };

  const getEstadoBadge = (estado, leido) => {
    if (!leido && estado === 'pendiente') {
      return <span className="badge-nueva">Nueva</span>;
    }
    const clases = {
      pendiente: 'badge-pendiente',
      en_proceso: 'badge-proceso',
      completado: 'badge-completado',
      cliente_creado: 'badge-cliente'
    };
    const textos = {
      pendiente: 'Pendiente',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cliente_creado: 'Cliente Creado'
    };
    return <span className={clases[estado] || 'badge-pendiente'}>{textos[estado] || estado}</span>;
  };

  const solicitudesFiltradas = filter === 'todas' 
    ? solicitudes 
    : solicitudes.filter(s => s.estado === filter);

  if (loading) return <div className="loading">Cargando solicitudes...</div>;

  return (
    <AdminLayout>
      <div className="solicitudes-admin">
        <div className="admin-navigation">
          <BackButton to="/dashboard" label="Volver al Dashboard" variant="outline" />
        </div>

        {/* Tarjetas de estadisticas */}
        <div className="estadisticas-grid">
          <div className="estadistica-card">
            <div className="estadistica-icono">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 6.5H2M22 12.5H2M22 18.5H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="estadistica-info">
              <h3>Total</h3>
              <p className="estadistica-numero">{estadisticas.total}</p>
            </div>
          </div>
          <div className="estadistica-card pendiente">
            <div className="estadistica-icono">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="estadistica-info">
              <h3>Pendientes</h3>
              <p className="estadistica-numero">{estadisticas.pendientes}</p>
            </div>
          </div>
          <div className="estadistica-card no-leida">
            <div className="estadistica-icono">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div className="estadistica-info">
              <h3>No leidas</h3>
              <p className="estadistica-numero">{estadisticas.noLeidas}</p>
            </div>
          </div>
          <div className="estadistica-card completada">
            <div className="estadistica-icono">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="estadistica-info">
              <h3>Completadas</h3>
              <p className="estadistica-numero">{estadisticas.completadas}</p>
            </div>
          </div>
        </div>

        <div className="solicitudes-header">
          <h1>Solicitudes de Contacto</h1>
        </div>

        <div className="solicitudes-filters">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="todas">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="en_proceso">En Proceso</option>
            <option value="completado">Completadas</option>
            <option value="cliente_creado">Clientes Creados</option>
          </select>
        </div>

        <div className="solicitudes-grid">
          {solicitudesFiltradas.length === 0 ? (
            <p className="no-results">No hay solicitudes</p>
          ) : (
            solicitudesFiltradas.map(solicitud => (
              <div key={solicitud.id} className={`solicitud-card ${!solicitud.leido ? 'no-leida' : ''}`}>
                <div className="solicitud-header">
                  <div className="solicitud-info">
                    <h3>{solicitud.nombre}</h3>
                    <p className="solicitud-contacto">
                      {solicitud.email} | {solicitud.telefono}
                    </p>
                    <p className="solicitud-documento">
                      Documento: {solicitud.documento}
                    </p>
                  </div>
                  <div className="solicitud-estado">
                    {getEstadoBadge(solicitud.estado, solicitud.leido)}
                  </div>
                </div>

                <div className="solicitud-body">
                  <p><strong>Tipo de Asesoria:</strong> {getTipoLabel(solicitud.tipoAsesoria)}</p>
                  {solicitud.mensaje && (
                    <p><strong>Mensaje:</strong> {solicitud.mensaje.substring(0, 100)}...</p>
                  )}
                  <p className="solicitud-fecha">
                    {solicitud.fechaSolicitud?.toLocaleDateString()} - {solicitud.fechaSolicitud?.toLocaleTimeString()}
                  </p>
                </div>

                <div className="solicitud-footer">
                  <button 
                    className="btn-ver-detalle"
                    onClick={() => handleVerDetalle(solicitud)}
                  >
                    Ver detalle
                  </button>
                  <button 
                    className="btn-contactar"
                    onClick={() => handleContactar(solicitud)}
                  >
                    Contactar
                  </button>
                  {solicitud.estado !== 'cliente_creado' && solicitud.estado !== 'completado' && (
                    <button 
                      className="btn-crear-cliente"
                      onClick={() => handleCrearCliente(solicitud)}
                    >
                      Crear Cliente
                    </button>
                  )}
                  <select 
                    className="estado-select"
                    value={solicitud.estado}
                    onChange={(e) => handleCambiarEstado(solicitud.id, e.target.value)}
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="en_proceso">En Proceso</option>
                    <option value="completado">Completado</option>
                    <option value="cliente_creado">Cliente Creado</option>
                  </select>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de detalle de solicitud */}
      {modalDetalle && solicitudSeleccionada && (
        <div className="modal-overlay" onClick={() => setModalDetalle(false)}>
          <div className="modal-detalle" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalle de Solicitud</h2>
              <button className="modal-close" onClick={() => setModalDetalle(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detalle-campo">
                <label>Nombre Completo:</label>
                <p>{solicitudSeleccionada.nombre}</p>
              </div>
              <div className="detalle-campo">
                <label>Documento:</label>
                <p>{solicitudSeleccionada.documento}</p>
              </div>
              <div className="detalle-campo">
                <label>Correo Electronico:</label>
                <p>{solicitudSeleccionada.email}</p>
              </div>
              <div className="detalle-campo">
                <label>Telefono:</label>
                <p>{solicitudSeleccionada.telefono}</p>
              </div>
              <div className="detalle-campo">
                <label>Tipo de Asesoria:</label>
                <p>{getTipoLabel(solicitudSeleccionada.tipoAsesoria)}</p>
              </div>
              <div className="detalle-campo">
                <label>Mensaje:</label>
                <p className="detalle-mensaje">{solicitudSeleccionada.mensaje || 'Sin mensaje adicional'}</p>
              </div>
              <div className="detalle-campo">
                <label>Fecha de solicitud:</label>
                <p>{solicitudSeleccionada.fechaSolicitud?.toLocaleString()}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-contactar-modal"
                onClick={() => {
                  handleContactar(solicitudSeleccionada);
                  setModalDetalle(false);
                }}
              >
                Contactar ahora
              </button>
              <button 
                className="btn-cerrar-modal"
                onClick={() => setModalDetalle(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        autoClose={true}
      />
    </AdminLayout>
  );
};

export default SolicitudesAdmin;